import Skeleton from "@components/scheduling/analytics/Skeleton";
import { formatCount } from "@components/scheduling/analytics/format";
import { COLORS } from "@components/scheduling/analytics/chartTheme";
import { OUTCOME_LABELS } from "@components/scheduling/campaignLabels";

const OUTCOME_COLOURS = {
  CONFIRMED: COLORS.booked,
  VOICEMAIL: COLORS.voicemail,
  FAILED: COLORS.failed,
};

/**
 * One labelled bar per outcome, biggest first, scaled to the biggest. The
 * label carries identity; the colour only says booked, voicemail or failed.
 */
export default function OutcomeBars({ outcomes, loading = false }) {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3" aria-busy="true">
        <Skeleton className="h-4 w-24" />
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
      </div>
    );
  }
  const rows = (Array.isArray(outcomes) ? outcomes : [])
    .filter((row) => row && row.outcome)
    .map((row) => ({ outcome: String(row.outcome), count: Number(row.count) || 0 }))
    .sort((a, b) => b.count - a.count);
  const max = rows.reduce((acc, row) => Math.max(acc, row.count), 0);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-medium text-gray-900">Outcomes</h3>
      {rows.length === 0 || max === 0 ? (
        <div className="mt-4 flex h-40 items-center justify-center rounded-lg border border-dashed border-gray-300">
          <p className="text-sm text-gray-500">No outcomes yet</p>
        </div>
      ) : (
        <ul
          role="img"
          aria-label={`Outcomes: ${rows.map((r) => `${formatCount(r.count)} ${OUTCOME_LABELS[r.outcome] || r.outcome}`).join(", ")}`}
          className="mt-3 space-y-2"
        >
          {rows.map((row) => (
            <li key={row.outcome} className="grid grid-cols-[10rem_1fr_3.5rem] items-center gap-3 text-sm">
              <span data-testid="outcome-label" className="truncate text-gray-700"
                    title={OUTCOME_LABELS[row.outcome] || row.outcome}>
                {OUTCOME_LABELS[row.outcome] || row.outcome}
              </span>
              <div className="h-4 w-full rounded bg-gray-100">
                <div
                  data-testid={`outcome-bar-${row.outcome}`}
                  className="h-4 rounded"
                  style={{
                    width: `${(row.count / max) * 100}%`,
                    backgroundColor: OUTCOME_COLOURS[row.outcome] || COLORS.neutral,
                  }}
                />
              </div>
              <span className="text-right font-medium text-gray-900">{formatCount(row.count)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
